import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import { LinearProgress } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { Formik, useFormikContext } from 'formik'

import createLogger from '~/src/Lib/Logging'
import { idPropType } from '~/src/Lib/PropTypes'
import { EMPTY_ARRAY, useResizeObserver } from '~/src/Lib/Utils'
import Button from '~/src/UI/Shared/Button'
import MentionField from '~/src/UI/Shared/MentionField'
import CommentWidget from '~/src/UI/Widgets/V2/CommentWidget'

const displayName = 'ViewComments'
const logger = createLogger(displayName)

export const styles = theme => ({
  root: {
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(2),
    flex: '1 1 100%',
    display: 'flex',
    flexDirection: 'column',
  },
  commentList: {
    flex: '1 1 100%',
  },
  addCommentInput: {
    position: 'sticky',
    bottom: 0,
    display: 'flex',
    flex: '0 0 auto',
    gap: '1rem',
    width: 'auto',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    whiteSpace: 'pre-line',
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.contrast.disabled}`,
    '&:hover': {
      borderColor: theme.palette.contrast.muted
    },
    '&:focus': {
      borderColor: theme.palette.blue.main
    },
    '&:after': {
      content: '""',
      backgroundColor: theme.palette.background.paper,
      position: 'absolute',
      bottom: -1,
      left: 0,
      height: 8,
      width: '100%',
      transform: 'translateY(100%)',
    },
    '& .MuiTypography-body2': {
      fontSize: '1rem',
      '& span': {
        fontSize: '1rem',
      },
    },
    '& .aroya__control': {
      minHeight: 0,
      fontSize: '1rem'
    },
    '& .aroya__control > textarea': {
      border: 'none',
      borderRadius: 0,
      paddingRight: 72,
    },
  },
  saveButton: {
    flex: '0 0 auto',
    height: 34.5
  }
})

const useStyles = makeStyles(styles)

export const ViewComments = () => {
  const {
    entities,
    doCommentDelete,
    doCommentEdit,
    doCommentSend,
  } = useConnect(
    'selectEntities',
    'selectUsers',
    'doCommentDelete',
    'doCommentEdit',
    'doCommentSend'
  )

  const classes = useStyles()
  const [commentText, setCommentText] = useState('')
  const [editing, setEditing] = useState(null)
  const [savingComment, setSavingComment] = useState(false)

  const { ref: formRef, height: formHeight } = useResizeObserver()
  const commentListRef = React.useRef()
  const reqRef = React.useRef()
  const { setValues, setFieldValue, values } = useFormikContext()
  const { comments: formikComments = EMPTY_ARRAY, id: formikTaskId } = values

  const onDelete = useCallback(async comment => {
    const result = await doCommentDelete(comment)
    if (!result) return
    setValues(({ comments, ...rest }) => ({
      ...rest,
      comments: comments.filter(commentId => commentId !== comment.id)
    }))
  }, [doCommentDelete, setValues])

  const comments = useMemo(
    () => formikComments.map(id => entities.comments?.[id]).filter(Boolean),
    [entities.comments, formikComments]
  )

  const handleCommentChange = useCallback((e, value, body, mentions) => {
    reqRef.current = { value, body, mentions }
    setCommentText(e.target.value)
  }, [])

  const handleSaveClick = useCallback(async () => {
    setSavingComment(true)
    const result = await doCommentSend({
      note: formikTaskId,
      value: reqRef.current.value,
      body: reqRef.current.body,
      mentions: reqRef.current.mentions,
    })
    setFieldValue('comments', [...formikComments, result.id])
    setSavingComment(false)
    setCommentText('')
  }, [doCommentSend, formikComments, formikTaskId, setFieldValue])

  // eslint-disable-next-line prefer-object-spread
  const listSpacerStyle = useMemo(() => Object.assign(
    { height: formHeight },
    savingComment
      ? {
        display: 'flex',
        alignItems: 'center',
        paddingInline: '25%',
      }
      : {}
  ), [formHeight, savingComment])

  useEffect(() => {
    if (!commentListRef.current || !comments.length || !formHeight) return
    const { current: scrollTarget } = commentListRef
    scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [comments.length, formHeight, savingComment])

  return (
    <div className={classes.root}>
      <div className={classes.commentList}>
        {comments?.length ? (
          comments.map(comment => (
            <CommentWidget
              key={comment.id}
              comment={comment}
              editing={editing}
              formikTaskId={formikTaskId}
              onDelete={onDelete}
              onEdit={doCommentEdit}
              setEditing={setEditing}
              titleProps={{ variant: 'subtitle2', color: 'textPrimary' }}
            />
          ))
        ) : null}
        <div style={listSpacerStyle}>
          {savingComment ? <LinearProgress /> : null}
        </div>
        <div ref={commentListRef} />
      </div>
      <div
        ref={formRef}
        className={classes.addCommentInput}
        aria-label="Task comment form"
      >
        <MentionField
          autoFocus
          data-testid="comment-input"
          disabled={savingComment}
          placeholder="Enter comment"
          suggestionsPortalHost={document.getElementById('mentionsRoot')}
          value={commentText}
          onChange={handleCommentChange}
          onSave={handleSaveClick}
        />
        <Button
          data-testid="comment-button"
          className={classes.saveButton}
          color="primary"
          size="small"
          disabled={!commentText || savingComment}
          onClick={handleSaveClick}
        >
          Save
        </Button>
      </div>
    </div>
  )
}

ViewComments.displayName = displayName

ViewComments.propTypes = {
  users: PropTypes.shape({
    user: idPropType.nullable
  }).isRequired,
}

const NoteViewCommentsWrapper = props => {
  const { notes, dialogRouteInfo } = useConnect('selectNotes', 'selectDialogRouteInfo')
  const note = notes[dialogRouteInfo?.params?.id] ?? {}

  const initialValues = {
    id: note?.id,
    comments: note?.comments
  }

  return (
    <Formik initialValues={initialValues}>
      <ViewComments {...props} />
    </Formik>
  )
}

export const NoteViewComments = React.memo(NoteViewCommentsWrapper)

const TaskViewCommentsWrapper = props => <ViewComments {...props} />
TaskViewCommentsWrapper.displayName = `Connected(${TaskViewCommentsWrapper.displayName})`

const TaskViewComments = React.memo(TaskViewCommentsWrapper)
export default TaskViewComments
